import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft, ChevronDown, Users, Flower2 } from "lucide-react";
import Wheel from "@/components/repro/Wheel";
import Weeks from "@/components/repro/Weeks";
import Compare from "@/components/repro/Compare";
import Sheet from "@/components/repro/Sheet";
import ReproHero from "@/components/repro/ReproHero";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";
import { STAGES, ATLAS_ASKED } from "@/lib/reproData";

const PAPER = "#FBF4F7";
const INK = "#3A1A2A";
const SOFT = "rgba(58,26,42,0.6)";
const HERO = "https://images.unsplash.com/photo-1615919737249-965d68c7e439?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

/* ------------------------------------------------- choosing your stage */
function Picker({ onPick }) {
  return (
    <div className="w-full" data-testid="repro-picker">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {STAGES.map((s, i) => (
          <motion.button key={s.key} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => onPick(s.key)}
            className="group text-left rounded-[1.6rem] p-5 relative overflow-hidden transition-all hover:-translate-y-1.5"
            style={{ background: "rgba(255,255,255,0.82)", border: "1px solid rgba(58,26,42,0.07)", backdropFilter: "blur(8px)", marginTop: i % 4 === 1 ? 20 : i % 4 === 2 ? 40 : i % 4 === 3 ? 14 : 0, boxShadow: "0 26px 50px -40px rgba(58,26,42,0.7)" }}
            data-testid={`stage-pick-${s.key}`}>
            <span className="absolute -right-10 -top-10 w-28 h-28 rounded-full transition-transform group-hover:scale-125" style={{ background: `radial-gradient(circle, ${s.tone}33, transparent 70%)` }} />
            <span className="relative block w-2.5 h-2.5 rounded-full" style={{ background: s.tone }} />
            <p className="relative font-display text-[21px] mt-3 leading-tight" style={{ color: INK }}>{s.name}</p>
            <p className="relative text-[13px] mt-1.5 leading-snug" style={{ color: SOFT }}>{s.line}</p>
            <span className="relative inline-flex items-center gap-1.5 text-[11.5px] font-semibold mt-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: s.tone }}>
              open this world <ArrowRight size={12} />
            </span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ the atlas */
function Chapters({ stage }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-2.5" data-testid="repro-chapters">
      {stage.chapters.map((c, i) => {
        const on = open === i;
        return (
          <motion.div key={c.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.06, 0.3) }}
            className="rounded-[1.5rem] overflow-hidden" style={{ background: on ? "#fff" : "rgba(255,255,255,0.68)", border: `1px solid ${on ? `${stage.tone}3A` : "rgba(58,26,42,0.07)"}`, boxShadow: on ? "0 34px 66px -50px rgba(58,26,42,0.8)" : "none", marginLeft: on ? 0 : i % 2 ? 18 : 0, transition: "margin 0.4s" }}
            data-testid={`chapter-${i}`}>
            <button onClick={() => setOpen(on ? -1 : i)} className="w-full flex items-start gap-4 px-6 py-5 text-left">
              <span className="font-display text-lg tabular-nums shrink-0" style={{ color: on ? stage.tone : "rgba(58,26,42,0.25)" }}>{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display text-[20px] sm:text-[23px] leading-tight flex-1" style={{ color: INK }}>{c.t}</span>
              <ChevronDown size={15} className="mt-1.5 shrink-0" style={{ color: "rgba(58,26,42,0.3)", transform: on ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
            </button>
            <AnimatePresence initial={false}>
              {on && (
                <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden px-6 pb-6 pl-[68px] text-[15.5px] leading-[1.85]" style={{ color: SOFT }}>{c.body}</motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}

function Instrument({ stage }) {
  if (stage.instrument === "wheel") return <Wheel tone={stage.tone} />;
  if (stage.instrument === "weeks") return <Weeks spec={stage.weeks} tone={stage.tone} />;
  if (stage.instrument === "compare") return <Compare spec={stage.compare} tone={stage.tone} />;
  return <Sheet spec={stage.sheet} tone={stage.tone} />;
}

const INSTRUMENT_TITLE = {
  wheel: ["The wheel", "One month, mapped, with the flags in the right places"],
  weeks: ["The timeline", "Drag it and see what is happening, and what to ask for"],
  compare: ["Side by side", "Every method on the same table, including what it costs you"],
  sheet: ["The sheet", "Tick what applies and take the list with you"],
};

function Asked() {
  const [on, setOn] = useState(0);
  return (
    <section style={{ background: PAPER }} data-testid="repro-asked">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(58,26,42,0.4)" }}>About this place</p>
        <h2 className="font-display text-[30px] sm:text-[40px] leading-[1.04] mt-2 mb-8" style={{ color: INK }}>Three things worth saying out loud</h2>
        <div className="space-y-2">
          {ATLAS_ASKED.map((e, i) => (
            <div key={e.q} className="rounded-2xl overflow-hidden" style={{ background: on === i ? "#fff" : "rgba(255,255,255,0.6)", border: "1px solid rgba(58,26,42,0.07)" }}>
              <button onClick={() => setOn(on === i ? -1 : i)} className="w-full flex items-center gap-4 px-6 py-4 text-left" data-testid={`repro-asked-${i}`}>
                <span className="font-display text-[18px] flex-1" style={{ color: INK }}>{e.q}</span>
                <ChevronDown size={15} style={{ color: "rgba(58,26,42,0.35)", transform: on === i ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
              </button>
              <AnimatePresence initial={false}>
                {on === i && (
                  <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden px-6 pb-5 text-[15px] leading-[1.85]" style={{ color: SOFT }}>{e.a}</motion.p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Reproductive() {
  const [data, patch] = useProgress("repro", { stage: null });
  const [entered, setEntered] = useState(false);
  const stage = entered ? STAGES.find((s) => s.key === data.stage) || null : null;
  const pick = (k) => { setEntered(true); patch({ stage: k }); };
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [data.stage]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const [title, sub] = stage ? INSTRUMENT_TITLE[stage.instrument] : [];

  return (
    <main className="pt-16" style={{ background: PAPER }} data-testid="platform-page-reproductive">
      {/* ============================== the stage chooser */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="repro-stage">
        <motion.div className="absolute -inset-y-[6%] inset-x-0" style={{ y: bgY, backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.5 }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(251,244,247,0.7) 0%, rgba(249,236,242,0.9) 44%, rgba(251,244,247,0.99) 88%, #FBF4F7 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(44% 40% at 84% 6%, rgba(222,110,150,0.3), transparent 72%), radial-gradient(42% 42% at 2% 84%, rgba(185,120,184,0.26), transparent 72%)" }} />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-16">
          <AnimatePresence mode="wait">
            {!stage ? (
              <motion.div key="pick" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -24, filter: "blur(8px)" }} transition={{ duration: 0.55 }}>
                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "rgba(58,26,42,0.45)" }}>Reproductive health · an atlas, not a feed</motion.p>
                <h1 className="font-display text-[38px] sm:text-[54px] lg:text-[64px] leading-[0.99] mt-5 max-w-3xl" style={{ color: INK }}>
                  {[["Tell", 0], ["me", 1], ["where", 2], ["you", 3], ["are,", 4], ["\n", -1], ["and", 5], ["everything", 6], ["else", 7], ["\n", -1], ["disappears.", 8]].map(([w, i], n) =>
                    w === "\n" ? <br key={n} /> : (
                      <motion.span key={n} initial={{ opacity: 0, y: 22, filter: "blur(7px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ delay: 0.16 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="inline-block mr-[0.24em]" style={{ color: i >= 5 ? "#B04068" : INK }}>{w}</motion.span>
                    ))}
                </h1>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="text-[16px] sm:text-[17.5px] mt-5 max-w-lg leading-relaxed" style={{ color: SOFT }}>
                  Eight stages, and only one of them is yours today. Nothing you choose leaves this device, and you may change it whenever you like.
                </motion.p>
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-9" data-testid="repro-hero-animation">
                  <ReproHero />
                </motion.div>
                <div className="mt-9"><Picker onPick={pick} /></div>
              </motion.div>
            ) : (
              <motion.div key={stage.key} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
                <button onClick={() => setEntered(false)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold transition-transform hover:-translate-x-0.5"
                  style={{ background: "rgba(255,255,255,0.85)", border: "1px solid rgba(58,26,42,0.08)", color: INK }} data-testid="repro-change-stage">
                  <ArrowLeft size={13} /> A different stage
                </button>
                <div className="mt-7 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                  <div className="lg:col-span-8">
                    <p className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: stage.tone }}>Your atlas · {stage.name}</p>
                    <h1 className="font-display text-[36px] sm:text-[50px] lg:text-[58px] leading-[1.0] mt-3" style={{ color: INK }}>{stage.line}</h1>
                    <p className="text-[15.5px] mt-4 max-w-xl" style={{ color: SOFT }}>{stage.who}</p>
                  </div>
                  <div className="lg:col-span-4 flex lg:justify-end">
                    <div className="flex flex-wrap gap-1.5">
                      {STAGES.filter((s) => s.key !== stage.key).map((s) => (
                        <button key={s.key} onClick={() => pick(s.key)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] transition-transform hover:-translate-y-0.5"
                          style={{ background: "rgba(255,255,255,0.75)", border: "1px solid rgba(58,26,42,0.07)", color: SOFT }} data-testid={`stage-chip-${s.key}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.tone }} />{s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ============================== chapters + instrument */}
      {stage && (
        <>
          <section style={{ background: PAPER }}>
            <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
              <p className="text-[10px] tracking-[0.24em] uppercase mb-6" style={{ color: "rgba(58,26,42,0.4)" }}>What is actually going on</p>
              <Chapters stage={stage} />
            </div>
          </section>

          <section className="relative overflow-hidden" style={{ background: `linear-gradient(170deg, ${stage.tone}1F, rgba(185,120,184,0.16))` }} data-testid="repro-instrument">
            <span className="absolute -left-24 top-0 w-[34vw] h-[34vw] rounded-full halo" style={{ background: `radial-gradient(circle, ${stage.tone}44, transparent 68%)`, filter: "blur(40px)" }} />
            <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20">
              <div className="mb-9 max-w-xl">
                <p className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: stage.tone }}>{title}</p>
                <h2 className="font-display text-[30px] sm:text-[42px] leading-[1.04] mt-2" style={{ color: INK }}>{sub}</h2>
              </div>
              <Instrument stage={stage} />
            </div>
          </section>
        </>
      )}

      {/* the room */}
      <section style={{ background: PAPER }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <div className="rounded-[2rem] p-8 sm:p-10 text-white relative overflow-hidden grain" style={{ background: `linear-gradient(140deg, ${stage?.tone || "#DE6E96"}, #7E3A64)` }} data-testid="repro-room">
            <span className="absolute -right-16 -top-16 w-56 h-56 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.3), transparent 70%)" }} />
            <div className="flex items-start gap-4 relative">
              <Users size={22} className="mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-2xl sm:text-3xl">A room per stage, so nobody explains themselves twice</h2>
                <p className="mt-2.5 max-w-xl text-[15px] opacity-85">You land among people who are already mid sentence about the same thing. Aliases only, and specialists answering under their real names.</p>
                <p className="mt-4 text-[12px] opacity-70">Opens with the full platform. Your alias already works here.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Asked />

      {!stage && (
        <section style={{ background: "linear-gradient(180deg, #FBF4F7, #F6E8EE)" }}>
          <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
            <div className="rounded-[2rem] px-7 py-9 flex flex-col sm:flex-row sm:items-center gap-5 relative overflow-hidden text-white" style={{ background: "linear-gradient(140deg, #DE6E96, #8E4B7C)" }}>
              <span className="absolute -right-16 -top-16 w-52 h-52 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.3), transparent 70%)" }} />
              <Flower2 size={26} className="shrink-0 relative" />
              <p className="font-display text-xl sm:text-[26px] leading-snug flex-1 relative">Pick a stage and the whole place rearranges itself around it.</p>
              <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="shrink-0 relative rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: "#fff", color: INK }} data-testid="repro-bottom-cta">
                Choose where I am
              </button>
            </div>
          </div>
        </section>
      )}

      <section style={{ background: "#F4E9EE", borderTop: "1px solid rgba(58,26,42,0.07)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4" style={{ color: "rgba(58,26,42,0.4)" }}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "reproductive").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5"
                style={{ background: "#fff", border: "1px solid rgba(58,26,42,0.08)", color: INK }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />{x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
